/**
 * Agent Atlas Service
 *
 * Thin business-logic layer over agentAtlasClient. Pages should consume
 * this, not the raw client, so we can layer caching / formatting here.
 */

import { agentAtlasAPI } from '../api/agentAtlasClient';

// Keep in sync with the surface manifests (agent-atlas surfaces/manifests/*.yaml),
// checked by agent-atlas scripts/check_surface_consumers.py. Colours were chosen
// to stay distinguishable beside their neighbours in every view (2026-09-29).
export const SURFACE_META = {
  'copilot':            { label: 'Microsoft 365 Copilot', color: '#3B82F6' },
  'copilot-studio':     { label: 'Copilot Studio',      color: '#06B6D4' },
  'gemini-enterprise':  { label: 'Gemini Enterprise',   color: '#E11D48' },
  'gemini-workspace':   { label: 'Gemini Workspace',    color: '#F87171' },
  'custom':             { label: 'Custom Agents',       color: '#A855F7' },
  'endpoint':           { label: 'Endpoint Security',   color: '#F59E0B' },
  // Old id of the same surface (renamed 2026-09-29). Kept so stored data and
  // saved links that still say "shadow" render; not an activity surface of its own.
  'shadow':             { label: 'Endpoint Security',   color: '#F59E0B' },
  // Cloud-deployed agents
  'gcp':                { label: 'GCP Agents',          color: '#4285F4' },
  'aws':                { label: 'AWS Agents',          color: '#FF9900' },
  'azure':              { label: 'Azure Agents',        color: '#0078D4' },
  'cloud-discovery':    { label: 'Cloud Discovery',     color: '#38BDF8' },
  // SaaS AI surfaces — keep in sync with SOURCES in the microservice sources.py.
  'openai':             { label: 'OpenAI',              color: '#10A37F' },
  'anthropic':          { label: 'Anthropic',           color: '#D97757' },
  'cursor':             { label: 'Cursor',              color: '#D4D4D8' },
  'google_antigravity': { label: 'Google Antigravity',  color: '#818CF8' },
};

// Surfaces the backend can actually report activity for, in display order.
// Derived from SURFACE_META so adding a surface in one place is enough —
// 'cloud-discovery' is a discovery lens, not a runtime activity surface.
export const ACTIVITY_SURFACES = Object.keys(SURFACE_META).filter((s) => s !== 'cloud-discovery' && s !== 'shadow');

// Coarse agent classification (custom / endpoint / saas / cloud) — keep in
// sync with AGENT_TYPE_BY_SURFACE in the microservice queries_agents.py.
export const AGENT_TYPE_META = {
  custom:   { label: 'Custom Agent',   color: '#A855F7', icon: 'code' },
  endpoint: { label: 'Endpoint Agent', color: '#F59E0B', icon: 'monitor' },
  saas:     { label: 'SaaS Agent',     color: '#3B82F6', icon: 'cloud' },
  cloud:    { label: 'Cloud Agent',    color: '#4285F4', icon: 'server' },
};

export const RISK_META = {
  critical: { label: 'Critical', color: '#EF4444', bg: 'rgba(239,68,68,0.12)'   },
  high:     { label: 'High',     color: '#F97316', bg: 'rgba(249,115,22,0.12)'  },
  medium:   { label: 'Medium',   color: '#EAB308', bg: 'rgba(234,179,8,0.12)'   },
  low:      { label: 'Low',      color: '#22C55E', bg: 'rgba(34,197,94,0.12)'   },
};

export const agentAtlasService = {
  getOverview:              (p) => agentAtlasAPI.getOverview(p),
  getUniverse:              (p) => agentAtlasAPI.getUniverse(p),
  getCoverage:              (p) => agentAtlasAPI.getCoverage(p),
  getAttention:             (p) => agentAtlasAPI.getAttention(p),
  getSeverityDistribution:  (p) => agentAtlasAPI.getSeverityDistribution(p),

  // ---- Atlas home (Today / Estate) ----
  /**
   * Agents first seen in the window, newest first (the count is the engine's agents.new).
   * @returns {Promise<{items: Array<{id: string, name: string, surface: string,
   *   surface_label: string, first_seen: string, owner: ?string}>}>}
   */
  getNewAgents:             (p) => agentAtlasAPI.getNewAgents(p),
  /**
   * Identities (people and non-human principals) first seen using an agent in the window,
   * newest first (the counts are the engine's identities.new / identities.new_non_human).
   * @returns {Promise<{items: Array<{id: string, label: string,
   *   kind: 'human'|'non_human', first_seen: string, surfaces: string[]}>}>}
   */
  getNewIdentities:         (p) => agentAtlasAPI.getNewIdentities(p),
  /**
   * Each surface feed's state. Takes {minutes}. (How many report and are connected is the
   * engine's feeds.reporting / feeds.connected.)
   * @returns {Promise<{window_minutes: number,
   *   items: Array<{surface: string, label: string, last_event_at: ?string, fresh: boolean,
   *   status: 'reporting'|'stale'|'no_agent_identity'|'silent', note: ?string}>}>}
   */
  getFeedsHealth:           (p) => agentAtlasAPI.getFeedsHealth(p),
  /**
   * Where each agent's accountable owner comes from. Ids match /universe agent ids.
   * @returns {Promise<{items: Array<{id: string, owner: ?string,
   *   owner_source: 'set'|'vendor'|'creator'|'unknown'}>}>}
   */
  getOwnerSources:          (p) => agentAtlasAPI.getOwnerSources(p),
  getCloudRisk:             (p) => agentAtlasAPI.getCloudRisk(p),
  listAgents:               (p) => agentAtlasAPI.listAgents(p),
  listAgentsPage:           (p) => agentAtlasAPI.listAgentsPage(p),
  getAgent:                 (id, p) => agentAtlasAPI.getAgent(id, p),
  getAgentTimeline:         (id, p) => agentAtlasAPI.getAgentTimeline(id, p),
  getAgentViolations:       (id, p) => agentAtlasAPI.getAgentViolations(id, p),
  getAgentPolicyViolations: (id, p) => agentAtlasAPI.getAgentPolicyViolations(id, p),
  // Agent onboarding. Records: {agent_id, state: 'discovered'|'claimed'|'onboarded', profile,
  // confirmed_steps, missing_steps, owner_email, owning_team, criticality, revision, exists, events}.
  getOnboardingSteps:       (p) => agentAtlasAPI.getOnboardingSteps(p),
  listOnboarding:           (p) => agentAtlasAPI.listOnboarding(p),
  getAgentOnboarding:       (id, p) => agentAtlasAPI.getAgentOnboarding(id, p),
  // Pre-fills ({field: {value, source}}), user/team pickers and read-only panels for one agent.
  getOnboardingContext:     (id, p) => agentAtlasAPI.getOnboardingContext(id, p),
  getOnboardingBundles:     (p) => agentAtlasAPI.getOnboardingBundles(p),
  composeOnboardingBundle:  (body) => agentAtlasAPI.composeOnboardingBundle(body),
  saveOnboardingBundle:     (body, id) => agentAtlasAPI.saveOnboardingBundle(body, id),
  archiveOnboardingBundle:  (id) => agentAtlasAPI.archiveOnboardingBundle(id),
  applyOnboardingBundle:    (id, body) => agentAtlasAPI.applyOnboardingBundle(id, body),
  reviewAgents:             (body) => agentAtlasAPI.reviewAgents(body),
  reopenAgentReview:        (body) => agentAtlasAPI.reopenAgentReview(body),
  claimAgent:               (id, body) => agentAtlasAPI.claimAgent(id, body),
  saveOnboardingStep:       (id, step, body) => agentAtlasAPI.saveOnboardingStep(id, step, body),
  completeOnboarding:       (id, body) => agentAtlasAPI.completeOnboarding(id, body),
  getOnboardingSetup:       (id, p) => agentAtlasAPI.getOnboardingSetup(id, p),
  runOnboardingSetup:       (id, body) => agentAtlasAPI.runOnboardingSetup(id, body),
  getAgentControls:         (id, p) => agentAtlasAPI.getAgentControls(id, p),
  suggestOutcomeChecks:     (id, text) => agentAtlasAPI.suggestOutcomeChecks(id, text),
  getAgentOutcome:          (id, p) => agentAtlasAPI.getAgentOutcome(id, p),
  loadPolicyIntoEngine:     (id, pid) => agentAtlasAPI.loadPolicyIntoEngine(id, pid),
  getViolationTrace:        (id, p) => agentAtlasAPI.getViolationTrace(id, p),
  listUsers:                (p) => agentAtlasAPI.listUsers(p),
  getUser:                  (email, p) => agentAtlasAPI.getUser(email, p),
  getResponsibleAIOverview:       (p) => agentAtlasAPI.getResponsibleAIOverview(p),
  getResponsibleAIPillars:        (p) => agentAtlasAPI.getResponsibleAIPillars(p),
  getResponsibleAIInsights:       (p) => agentAtlasAPI.getResponsibleAIInsights(p),
  getResponsibleAICoverageMatrix: (p) => agentAtlasAPI.getResponsibleAICoverageMatrix(p),
  getResponsibleAIPillarEvidence: (id, p) => agentAtlasAPI.getResponsibleAIPillarEvidence(id, p),
  getResponsibleAIFindings:       (p) => agentAtlasAPI.getResponsibleAIFindings(p),

  getResponsibleAIReport:         (p) => agentAtlasAPI.getResponsibleAIReport(p),
  getComplianceOverview:    (p) => agentAtlasAPI.getComplianceOverview(p),
  getComplianceFrameworks:  (p) => agentAtlasAPI.getComplianceFrameworks(p),
  getComplianceControls:    (p) => agentAtlasAPI.getComplianceControls(p),
  getComplianceSurfaces:    (p) => agentAtlasAPI.getComplianceSurfaces(p),
  getComplianceTrend:       (p) => agentAtlasAPI.getComplianceTrend(p),
  getResponsibleAISystems:  (p) => agentAtlasAPI.getResponsibleAISystems(p),
  getComplianceAuditReport: (p) => agentAtlasAPI.getComplianceAuditReport(p),
  getComplianceModules:     (p) => agentAtlasAPI.getComplianceModules(p),
  getCompliancePeriodComparison: (p) => agentAtlasAPI.getCompliancePeriodComparison(p),
  getComplianceUsers:       (p) => agentAtlasAPI.getComplianceUsers(p),
  getComplianceLogs:        (p) => agentAtlasAPI.getComplianceLogs(p),
  getPolicyOverview:        (p) => agentAtlasAPI.getPolicyOverview(p),
  getPolicyPlanes:          (p) => agentAtlasAPI.getPolicyPlanes(p),
  getPolicyActions:         (p) => agentAtlasAPI.getPolicyActions(p),
  listPolicies:             (p) => agentAtlasAPI.listPolicies(p),
  getPolicy:                (id, p) => agentAtlasAPI.getPolicy(id, p),
  listPolicyDecisions:      (p) => agentAtlasAPI.listPolicyDecisions(p),
  getPolicyTrend:           (p) => agentAtlasAPI.getPolicyTrend(p),
  getPolicyDecisionTrace:   (id, p) => agentAtlasAPI.getPolicyDecisionTrace(id, p),
  getAdoptionIntent:        (p) => agentAtlasAPI.getAdoptionIntent(p),
  getAdoptionKnowledgeGaps: (p) => agentAtlasAPI.getAdoptionKnowledgeGaps(p),
  getAdoptionSessions:      (p) => agentAtlasAPI.getAdoptionSessions(p),
  getAdoptionSession:       (p) => agentAtlasAPI.getAdoptionSession(p),
  getTraceConversations:    (p) => agentAtlasAPI.getTraceConversations(p),
  getTraceConversation:     (p) => agentAtlasAPI.getTraceConversation(p),
  lookupTraceConversation:  (p) => agentAtlasAPI.lookupTraceConversation(p),
  getTraceSpan:             (p) => agentAtlasAPI.getTraceSpan(p),
  getTraceDrift:            (p) => agentAtlasAPI.getTraceDrift(p),
  getAdoptionIntents:       (p) => agentAtlasAPI.getAdoptionIntents(p),
  getFinOpsSurfaces:        (p) => agentAtlasAPI.getFinOpsSurfaces(p),
  getFinOpsTrend:           (p) => agentAtlasAPI.getFinOpsTrend(p),
  getFinOpsCatalog:         (p) => agentAtlasAPI.getFinOpsCatalog(p),
  getFinOpsBudgets:         (p) => agentAtlasAPI.getFinOpsBudgets(p),
  getFinOpsBudget:          (id, p) => agentAtlasAPI.getFinOpsBudget(id, p),
  createFinOpsBudget:       (b, p) => agentAtlasAPI.createFinOpsBudget(b, p),
  updateFinOpsBudget:       (id, b, p) => agentAtlasAPI.updateFinOpsBudget(id, b, p),
  deleteFinOpsBudget:       (id, p) => agentAtlasAPI.deleteFinOpsBudget(id, p),
  getBudgetOptions:         (p) => agentAtlasAPI.getBudgetOptions(p),
  getBudgetEvents:          (p) => agentAtlasAPI.getBudgetEvents(p),
  getBudgetRequests:        (p) => agentAtlasAPI.getBudgetRequests(p),
  createBudgetRequest:      (b, p) => agentAtlasAPI.createBudgetRequest(b, p),
  decideBudgetRequest:      (id, b, p) => agentAtlasAPI.decideBudgetRequest(id, b, p),
  getFinOpsOverview:        (p) => agentAtlasAPI.getFinOpsOverview(p),
  getFinOpsBilling:         (p) => agentAtlasAPI.getFinOpsBilling(p),
  getFinOpsTokenomics:      (surface, p) => agentAtlasAPI.getFinOpsTokenomics(surface, p),
  getFinOpsPrices:          (p) => agentAtlasAPI.getFinOpsPrices(p),
  saveFinOpsPrices:         (b, p) => agentAtlasAPI.saveFinOpsPrices(b, p),
  getFinOpsRoi:             (p) => agentAtlasAPI.getFinOpsRoi(p),
  getFinOpsAssumptions:     (p) => agentAtlasAPI.getFinOpsAssumptions(p),
  saveFinOpsAssumptions:    (b, p) => agentAtlasAPI.saveFinOpsAssumptions(b, p),
  getFinOpsLicensing:       (p) => agentAtlasAPI.getFinOpsLicensing(p),
  getFinOpsContracts:       (p) => agentAtlasAPI.getFinOpsContracts(p),
  createFinOpsContract:     (b, p) => agentAtlasAPI.createFinOpsContract(b, p),
  updateFinOpsContract:     (id, b, p) => agentAtlasAPI.updateFinOpsContract(id, b, p),
  deleteFinOpsContract:     (id, p) => agentAtlasAPI.deleteFinOpsContract(id, p),
  getFinOpsSettings:        (p) => agentAtlasAPI.getFinOpsSettings(p),
  saveFinOpsSettings:       (b, p) => agentAtlasAPI.saveFinOpsSettings(b, p),
  getHeatmap:               (p) => agentAtlasAPI.getThreatSurfaceHeatmap(p),
  getDenyShadowFlow:        (p) => agentAtlasAPI.getDenyShadowFlow(p),
  getBlastRadius:           (p) => agentAtlasAPI.getBlastRadius(p),

  // Graph (Neo4j entity graph). Relationship views — one traversal each.
  getGraphStats:             (p) => agentAtlasAPI.getGraphStats(p),
  getGraphNetwork:           (p) => agentAtlasAPI.getGraphNetwork(p),
  getGraphAgents:            (p) => agentAtlasAPI.getGraphAgents(p),
  getGraphSurfaces:          (p) => agentAtlasAPI.getGraphSurfaces(p),
  getGraphAgentNeighborhood: (id, p) => agentAtlasAPI.getGraphAgentNeighborhood(id, p),
  getGraphNodeNeighborhood:  (id, p) => agentAtlasAPI.getGraphNodeNeighborhood(id, p),
  getGraphShadowAI:          (p) => agentAtlasAPI.getGraphShadowAI(p),
  getGraphModels:            (p) => agentAtlasAPI.getGraphModels(p),
  getGraphFrameworkExposure: (p) => agentAtlasAPI.getGraphFrameworkExposure(p),
  getGraphDetectionMap:      (p) => agentAtlasAPI.getGraphDetectionMap(p),
  getGraphPolicyViolations:  (p) => agentAtlasAPI.getGraphPolicyViolations(p),
  getGraphAlerts:            (p) => agentAtlasAPI.getGraphAlerts(p),
  getGraphRiskyAgents:       (p) => agentAtlasAPI.getGraphRiskyAgents(p),
  getGraphBlastRadius:       (p) => agentAtlasAPI.getGraphBlastRadius(p),
  getGraphAgentLineage:      (id, p) => agentAtlasAPI.getGraphAgentLineage(id, p),
};

export default agentAtlasService;

// ---- Formatters ----
export const formatNumber = (n) => {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US');
};

export const formatRelative = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1)    return 'just now';
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};
